import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DropdownProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// Custom Dropdown component for scrollable month/year selection
function CustomDropdown(props: DropdownProps) {
  const { value, onChange, children, ...selectProps } = props;
  
  return (
    <Select 
      value={value?.toString()} 
      onValueChange={(value) => {
        const changeEvent = {
          target: { value }
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange?.(changeEvent);
      }}
    >
      <SelectTrigger className="w-auto min-w-[90px] gap-1 border border-border/40 rounded-md p-2 text-sm bg-background hover:bg-accent/5 focus:bg-accent/10 transition-colors">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        <ScrollArea className="h-72">
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === 'option') {
              return (
                <SelectItem key={child.props.value} value={child.props.value?.toString() || ''}>
                  {child.props.children}
                </SelectItem>
              );
            }
            return null;
          })}
        </ScrollArea>
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  weekStartsOn = 1,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      className={cn("p-3 flex justify-center", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 flex flex-col items-center",
        caption: "flex justify-center items-center gap-2 pt-1 pb-3 relative w-full max-w-[280px]",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-0 z-10",
        nav_button_next: "absolute right-0 z-10",
        table: "w-auto border-collapse space-y-1 mx-auto",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] flex items-center justify-center",
        row: "flex mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
        Dropdown: CustomDropdown,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
